import { action } from "mobx";
import { useState, useEffect, useMemo } from "react";

import { 
  Box, CircularProgress, IconButton, Typography, Button, 
  DialogActions, DialogContent, DialogTitle, List, 
  ListItemText, ListItemButton, ListItemIcon, TextField, 
  Grid, Divider, Breadcrumbs, Link
} from "@mui/material";

import DialogNewProject from "./DialogCreateNewProject";
import DialogNewFolder from "./DialogNewFolder";

import { useViewState } from "../model/ViewState";
import { getProject } from "../operations/ProjectOperations";
import { useProjectStore } from "../model/ProjectStore";
import { BootstrapDialog } from "../util/Styles";
import { moveFolderAPI, moveFileAPI, createFolderAPI, deleteFolderAPI, getProjectList } from "../operations/ProjectManagementOperations";

import AssignmentIcon from '@mui/icons-material/Assignment';
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from '@mui/icons-material/Folder';

import { observer } from "mobx-react";
import toast from "react-hot-toast";
import ProjectItem from "../model/ProjectItem";
import { ProjectItemType } from "../model/ProjectItem";

function FileExplorerPath({ path = [], onSegmentClick }) {
  const handleSegmentClick = (event, segmentName, index) => {
    event.preventDefault();
    if (onSegmentClick) {
      onSegmentClick(segmentName, index);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        bgcolor: 'background.paper', 
        p: 1, 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        mb: 2,
        overflowX: 'auto', 
        whiteSpace: 'nowrap'
      }}
    >
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />} 
        aria-label="breadcrumb"
      >
        <Link
          component="button"
          underline="hover"
          color={path.length === 0 ? "text.primary" : "inherit"}
          onClick={(e) => handleSegmentClick(e, "Root", -1)}
          disabled={path.length === 0}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '0.875rem',
            fontWeight: path.length === 0 ? 600 : 400,
            cursor: path.length === 0 ? 'default' : 'pointer',
            border: 'none',
            bgcolor: 'transparent',
            p: 0
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Root
        </Link>

        {path.map((segment, index) => {
          const isLast = index === path.length;

          return isLast ? (
            <Typography 
              key={index} 
              color="text.primary" 
              sx={{ 
                fontSize: '0.875rem', 
                fontWeight: 600 
              }}
            >
              {segment}
            </Typography>
          ) : (
            <Link
              key={index}
              component="button"
              underline="hover"
              color="inherit"
              onClick={(e) => handleSegmentClick(e, segment, index)}
              sx={{ 
                fontSize: '0.875rem',
                border: 'none',
                bgcolor: 'transparent',
                p: 0,
                cursor: 'pointer'
              }}
            >
              {segment}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}

function DialogOpenProjectWithFolders({ open, onFinish }) {
  const [createNewFolderDialogOpen, setCreateNewFolderDialogOpen] = useState(false);
  const [isFetchingProjects, setIsFetchingProjects] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [createNewProjectDialogOpen, setCreateNewProjectDialogOpen] = useState(false);
  
  const [rootFolder, setRootFolder] = useState([]); //ProjectItem[]
  const [currentFolder, setCurrentFolder] = useState([]); //ProjectItem[]
  const [path, setPath] = useState([]); //string[]
  
  const view = useViewState();
  const store = useProjectStore();
  const t = (val) => {return val;};

  useEffect(() => {
    const getProjects = async () => {
      const responseData = await getProjectList();
      if (responseData && Array.isArray(responseData)) {
        const models = responseData.map(item => ProjectItem.fromJS(item));
        setRootFolder(models);
        setCurrentFolder(models);
      }
      setIsFetchingProjects(false);
    };

    if (open) {
      setIsFetchingProjects(true);
      getProjects();
    }
  }, [open]);

  // Drag and drop handlers
  const handleDragStart = (e, item) => { //e React.DragEvent item ProjectItem
    const dragPayload = {
      id: item.id,
      name: item.name,
      isFolder: item.type === ProjectItemType.Folder
    };
    e.dataTransfer.setData("application/json", JSON.stringify(dragPayload));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnFolder = async (e, targetFolder, targetLabel) => { //targetFolder ProjectItem targetLabel string
    e.preventDefault();
    try {
      const rawData = e.dataTransfer.getData("application/json");
      if (!rawData) return;
      
      const draggedItem = JSON.parse(rawData);

      if (draggedItem.isFolder && draggedItem.name === targetLabel) {
        console.warn("Cannot drop a folder into itself.");
        return;
      }

      let destinationPath = "";
    
      if (targetLabel === "..") {
        destinationPath = path.slice(0, -1).join("/") || "root";
      } else if (targetLabel === "root") {
        destinationPath = "root";
      } else {
        const currentPathString = path.join("/");
        destinationPath = currentPathString ? `${currentPathString}/${targetLabel}` : targetLabel;
      }

      const sourceParentPath = path.join("/") || "root";

      setIsFetchingProjects(true);
      
      let response;
      if (!draggedItem.isFolder) {
        response = await moveFileAPI(sourceParentPath, draggedItem.name, destinationPath);
      } else {
        response = await moveFolderAPI(sourceParentPath, draggedItem.name, destinationPath);
      }

      if (response && response.success) {
        const freshData = await getProjectList();
        if (freshData && Array.isArray(freshData)) {
          const models = freshData.map(item => ProjectItem.fromJS(item));
          setRootFolder(models);
          updateFolderFromPath(path, models);
          toast.success("Item moved successfully!");
        }
      } else {
        toast.error(`Failed to move item: ${response?.error}`);
      }
      setIsFetchingProjects(false);
    } catch (err) {
      console.error("Failed executing drop routing: ", err);
    }
  };

  const renderFolder = (items) => { //items ProjectItem[]
    if (items && items.length > 0) {
      const sortedItems = [...items].sort((a, b) => a.type - b.type);

      const renderedItems = sortedItems.map((item) => {
        if (item.type == ProjectItemType.Project) {
          return (
            <ListItemButton 
              key={item.id} 
              onClick={() => handleProjectClick(item)}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              sx={{ 
                mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'grab',  
                "&:hover": { borderColor: 'primary.main', bgcolor: 'action.hover' },
                "&:active": { cursor: 'grabbing' }, width: 500, boxSizing: 'border-box' 
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AssignmentIcon fontSize="small" color="primary" /> 
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body1" title={item.name} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.name}
                  </Typography>
                }
              />
            </ListItemButton>
          );
        } else {
          return (
            <ListItemButton 
              key={item.id || item.name} 
              onClick={() => handleFolderClick(item)}
              draggable
              onDragStart={(e) => handleDragStart(e, item)} 
              onDragOver={handleDragOver} 
              onDrop={(e) => handleDropOnFolder(e, item, item.name)}
              sx={{ 
                mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'grab',
                "&:hover": { borderColor: 'primary.main', bgcolor: 'action.hover', "& .delete-btn": { opacity: 1 } },
                "&:active": { cursor: 'grabbing' }, width: 500, boxSizing: 'border-box',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flexGrow: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderIcon fontSize="small" color="primary" /> 
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="body1" title={item.name} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.name}
                    </Typography>
                  }
                />
              </Box>

              <IconButton
                className="delete-btn"
                size="small"
                onClick={(e) => handleDeleteFolder(e, item.name)}
                sx={{ opacity: 0, transition: 'opacity 0.2s', color: 'text.secondary', "&:hover": { color: 'error.main' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          );
        }
      });

      if (path.length > 0) {
        renderedItems.unshift(
          <ListItemButton 
            key="go-back-directory" 
            onClick={() => handlePathSegmentClick("", path.length - 2)}
            sx={{ 
              mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', fontStyle: 'italic',
              "&:hover": { borderColor: 'primary.main', bgcolor: 'action.hover' }, width: 330, boxSizing: 'border-box' 
            }}
            onDragOver={handleDragOver} 
            onDrop={(e) => handleDropOnFolder(e, null, "..")}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <FolderIcon fontSize="small" color="action" /> 
            </ListItemIcon>
            <ListItemText primary={<Typography variant="body1" sx={{ fontWeight: 600 }}>..</Typography>} />
          </ListItemButton>
        );
      }
      return renderedItems;
    }

    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        No projects or folders found.
      </Typography>
    );
  };

  const handleProjectClick = async (project) => { //project ProjectItem
    setIsLoading(true);
    view.reset();
    sessionStorage.setItem("paratto_project_id", JSON.stringify(project.id));
    
    try {
      const projectData = await getProject(project.id);
      store.dispose();
      store.updateStore(projectData);
      closeDialog();
    } catch (error) {
      console.error("Failed to load project: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderClick = (folder) => { //folder ProjectItem
    const updatedPath = [...path, folder.name];
    setPath(updatedPath);
    setCurrentFolder(folder.items || []);
  };

  const closeDialog = action(() => {
    view.selectedMaterialId = "";
    view.selectedRiskId = "";
    view.selectedTaskId = "";
    view.loadProjectDialogOpen = false;
    setPath([]);
  });

  const handlePathSegmentClick = (segmentName, index) => { //string, number
    if (index === -1) {
      setPath([]);
      setCurrentFolder(rootFolder);
    } else {
      const newPath = path.slice(0, index + 1);
      setPath(newPath);
      updateFolderFromPath(newPath, rootFolder);
    }
  };

  const updateFolderFromPath = (pathStrings, targetData = rootFolder) => { //pathString = string[]
    let currentLevelItems = targetData;
    
    for (const segment of pathStrings) {
      const foundFolder = currentLevelItems.find(
        item => item.name.toLowerCase() === segment.toLowerCase() && item.type === ProjectItemType.Folder
      );
      if (foundFolder) {
        currentLevelItems = foundFolder.items || [];
      } else {
        break;
      }
    }
    setCurrentFolder(currentLevelItems);
  };

  const handleCreateFolderComplete = async (newFolderName) => { //string
    setCreateNewFolderDialogOpen(false);
    const parentPath = path.join("/") || "root";

    try {
      setIsLoading(true);
      const response = await createFolderAPI(parentPath, newFolderName);
      
      if (response && response.success) {
        const freshData = await getProjectList();
        if (freshData && Array.isArray(freshData)) {
          const models = freshData.map(item => ProjectItem.fromJS(item));
          setRootFolder(models);
          updateFolderFromPath(path, models);
          toast.success("Folder created successfully!");
        }
      } else {
        toast.error(`Failed to create folder: ${response?.error}`);
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = async (e, folderName) => { //React.MouseEvent, string
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete "${folderName}"?`)) {
      return;
    }

    const parentPath = path.join("/") || "root";

    try {
      setIsLoading(true);
      const response = await deleteFolderAPI(parentPath, folderName);

      if (response && response.success) {
        toast.success("Folder deleted successfully!");
        const freshData = await getProjectList();
        if (freshData && Array.isArray(freshData)) {
          const models = freshData.map(item => ProjectItem.fromJS(item));
          setRootFolder(models);
          updateFolderFromPath(path, models);
        }
      } else {
        toast.error(`Cannot delete folder: ${response?.error || "Ensure it is empty."}`);
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
      toast.error("Failed to delete folder.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogNewProject open={createNewProjectDialogOpen} onCancel={() => setCreateNewProjectDialogOpen(false)} />
      <DialogNewFolder open={createNewFolderDialogOpen} onComplete={handleCreateFolderComplete} onCancel={() => setCreateNewFolderDialogOpen(false)} />
      
      <BootstrapDialog open={open} onClose={isLoading ? undefined : closeDialog} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">{t("Open Project")}</Typography>
        </DialogTitle>
        
        <IconButton disabled={isLoading} onClick={closeDialog} sx={{ position: "absolute", right: 8, top: 8, color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>

        <FileExplorerPath path={path} onSegmentClick={handlePathSegmentClick}/>

        <DialogContent dividers sx={{ minHeight: 400, bgcolor: 'background.default', position: 'relative' }}>
          {
          (isFetchingProjects || isLoading) ? (
            <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={32} />
              <Typography sx={{ mt: 2 }} color="text.secondary">
                {isFetchingProjects ? "Loading workspace..." : "Loading..."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: '100%' }}>
              {renderFolder(currentFolder)}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'background.default', display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" color="primary" onClick={() => setCreateNewFolderDialogOpen(true)} disabled={isFetchingProjects}>
            {t("Add Folder")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={() => setCreateNewProjectDialogOpen(true)} disabled={isFetchingProjects}>
            {t("Create New Project")}
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
}

export default observer(DialogOpenProjectWithFolders);